import {Eyebrow, FadeUp} from './EducationShell';
import styles from './Step8Viz.module.css';
import {useRevealOnView} from './useRevealOnView';

/** Tiny "Zz" SVG that sits inside a sleep block to communicate "actual sleep". */
const ZzGlyph = ({size = 12}: {size?: number}) => (
  <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
    <text x="3" y="6" fontSize="6.5" fontWeight="700" fill="currentColor">
      z
    </text>
    <text x="6.5" y="11" fontSize="8.5" fontWeight="700" fill="currentColor">
      z
    </text>
  </svg>
);

const TONIGHT_BLOCKS = 10;
const BRANDED_TREATMENT_BLOCKS = 5;
const COMPOUNDED_TREATMENT_BLOCKS = 6;

type SleepBlocksVariant = 'branded' | 'compounded';

/**
 * Card 1: TONIGHT (10 blocks + 9 ticks) vs treatment row (fewer blocks =
 * fewer interruptions, longer rest). Branded shows 5 (~55% reduction,
 * SURMOUNT-OSA); compounded shows 6 (~26% per 10% body-weight loss).
 */
export const SleepBlocksViz = ({
  delayMs = 0,
  variant = 'branded',
}: {
  delayMs?: number;
  variant?: SleepBlocksVariant;
}) => {
  const treatmentBlocks = variant === 'compounded' ? COMPOUNDED_TREATMENT_BLOCKS : BRANDED_TREATMENT_BLOCKS;
  const treatmentLabel = variant === 'compounded' ? 'With weight loss' : 'On treatment';
  return (
    <div className={styles.vizRoot}>
      <FadeUp delayMs={delayMs}>
        <Eyebrow>Tonight</Eyebrow>
      </FadeUp>
      <FadeUp delayMs={delayMs + 80} className={styles.tonightRow}>
        <div className={styles.blockRow}>
          {Array.from({length: TONIGHT_BLOCKS}).map((_, i) => (
            <span key={`t-${i}`} className={styles.tonightCell}>
              <span className={styles.tonightBlock} />
              {i < TONIGHT_BLOCKS - 1 ? <span className={styles.tick} /> : null}
            </span>
          ))}
        </div>
      </FadeUp>

      <div className={styles.spacer} />

      <FadeUp delayMs={delayMs + 240}>
        <Eyebrow>{treatmentLabel}</Eyebrow>
      </FadeUp>
      <FadeUp delayMs={delayMs + 320} className={styles.treatmentRow}>
        <div className={styles.blockRow}>
          {Array.from({length: treatmentBlocks}).map((_, i) => (
            <span key={`tr-${i}`} className={styles.treatmentCell} style={{flex: 1}}>
              <span className={styles.treatmentBlock}>
                <span className={styles.zz} aria-hidden="true">
                  <ZzGlyph />
                </span>
              </span>
              {i < treatmentBlocks - 1 ? <span className={styles.tick} /> : null}
            </span>
          ))}
        </div>
      </FadeUp>

      <FadeUp delayMs={delayMs + 480} className={styles.legendRow}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSleep}`} />
          <span className={styles.legendLabel}>Sleep</span>
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendInterrupt}`} />
          <span className={styles.legendLabel}>Interruption</span>
        </span>
      </FadeUp>

      {variant === 'compounded' ? (
        <FadeUp delayMs={delayMs + 560} className={styles.illustrativeRow}>
          <span className={styles.illustrativeText}>Illustrative comparison for educational purposes</span>
        </FadeUp>
      ) : null}
    </div>
  );
};

/**
 * Card 2: time-in-bed quality bars. Tonight = 33% restorative, on
 * treatment = 67% restorative. Width animates on viewport entry.
 */
export const TimeInBedViz = ({delayMs = 0}: {delayMs?: number}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div ref={ref} className={styles.vizRoot}>
      <FadeUp delayMs={delayMs}>
        <Eyebrow>Tonight</Eyebrow>
      </FadeUp>
      <FadeUp delayMs={delayMs + 80} className={styles.qualityRow}>
        <div className={styles.qualityTrack}>
          <span
            className={styles.qualityFill}
            style={{width: triggered ? '33%' : '0%', transitionDelay: `${delayMs}ms`}}
          />
        </div>
      </FadeUp>

      <div className={styles.spacer} />

      <FadeUp delayMs={delayMs + 240}>
        <Eyebrow>On treatment</Eyebrow>
      </FadeUp>
      <FadeUp delayMs={delayMs + 320} className={styles.qualityRow}>
        <div className={styles.qualityTrack}>
          <span
            className={`${styles.qualityFill} ${styles.qualityFillTreated}`}
            style={{width: triggered ? '67%' : '0%', transitionDelay: `${delayMs + 200}ms`}}
          >
            <span className={styles.qualityZz} aria-hidden="true">
              <ZzGlyph />
            </span>
          </span>
        </div>
      </FadeUp>
    </div>
  );
};
